import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Paper, Box, Typography, useTheme, Chip } from '@mui/material';
import { IconButton } from '@mui/material';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';
import { ConfirmDeleteModal } from '@verantos-dev/ui-annotation-tool';

import { ResolvedCard } from './ResolvedCard';
import { useResolvedConceptCard } from './useResolvedConceptCard';
import { useSupervisorMode } from '../../../../context/SupervisorModeContext';
import { ResolvedConceptAnnotations } from '../../../../types/types';
import { InEditAnnotationCardOuter } from '../EditAndAddAnnotations/InEditAnnotationCardOuter';
import { InProgressAnnotationCard } from '../EditAndAddAnnotations/InProgressAnnotationCard';

interface ResolvedConceptCardProps {
  resolvedInConcept: ResolvedConceptAnnotations;
}

export const ResolvedConceptCard = ({ resolvedInConcept }: ResolvedConceptCardProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { isReadOnly } = useSupervisorMode();
  const {
    isExpanded,
    handleSetIsExpanded,
    annotationIdsInEditMode,
    handleDeleteResolvedAnnotation,
    handleConfirmDelete,
    showDeleteModal,
    setShowDeleteModal,
    handleDismissDeleteError,
    isDeleteResolvedAnnotationLoading,
    deleteResolvedAnnotationErrors,
    shouldShowCreateButton,
    isInFinalizedCreateMode,
    handleStartFinalizedCreate,
    isInResolvedReplaceMode,
    resolvedAnnotationIdToDelete,
  } = useResolvedConceptCard({
    conceptId: resolvedInConcept.conceptId,
    annotations: resolvedInConcept.annotations,
  });

  const hasResolvedFromConflict = resolvedInConcept.annotations.some(
    (annotation) => annotation.isResolvedFromConflict
  );
  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 0,
        paddingX: theme.spacing(4),
        paddingY: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.grey[300]}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography variant="body1">{resolvedInConcept.conceptName}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <Chip
            variant="filled"
            sx={{
              backgroundColor: hasResolvedFromConflict
                ? theme.palette.success.main
                : theme.palette.divider,
              size: 'small',
              fontSize: '13px',
              height: '18px',
              color: hasResolvedFromConflict
                ? theme.palette.success.contrastText
                : theme.palette.warning.contrastText,
            }}
            label={resolvedInConcept.annotations.length}
          />

          {isExpanded ? (
            <IconButton onClick={() => handleSetIsExpanded(false)}>
              <KeyboardArrowUp />
            </IconButton>
          ) : (
            <IconButton onClick={() => handleSetIsExpanded(true)}>
              <KeyboardArrowDown />
            </IconButton>
          )}
        </Box>
      </Box>

      {isExpanded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
          {resolvedInConcept.annotations.map((annotation, idx) => {
            if (
              isInResolvedReplaceMode &&
              annotation.annotationId === resolvedAnnotationIdToDelete
            ) {
              return null;
            }
            return !isReadOnly && annotationIdsInEditMode.includes(annotation.annotationId) ? (
              <InEditAnnotationCardOuter
                key={annotation.annotationId}
                annotation={annotation}
                conceptId={resolvedInConcept.conceptId}
                annotationNumber={idx + 1}
                onDelete={handleDeleteResolvedAnnotation}
                isDeleteResolvedAnnotationLoading={isDeleteResolvedAnnotationLoading}
              />
            ) : (
              <ResolvedCard
                key={annotation.annotationId}
                annotationNumber={idx + 1}
                annotation={annotation}
                conceptId={resolvedInConcept.conceptId}
                onDelete={handleDeleteResolvedAnnotation}
                isDeleteResolvedAnnotationLoading={isDeleteResolvedAnnotationLoading}
                deleteError={deleteResolvedAnnotationErrors[annotation.annotationId] ?? null}
                onDismissDeleteError={() => handleDismissDeleteError(annotation.annotationId)}
              />
            );
          })}

          {!isReadOnly && isInResolvedReplaceMode && resolvedAnnotationIdToDelete && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
                border: `1px solid ${theme.palette.divider}`,
                p: theme.spacing(4),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                <Typography variant="body1" sx={{ fontWeight: '500' }}>
                  Supervisor
                </Typography>
                <Chip
                  variant="filled"
                  sx={{ backgroundColor: theme.palette.grey[200], fontSize: '13px' }}
                  label={formatMessage({
                    id: 'workspaceCommon.annotationConfig.conceptCard.inProgress',
                  })}
                />
              </Box>
              <InProgressAnnotationCard
                annotation={null}
                conceptId={resolvedInConcept.conceptId}
                resolvedAnnotationIdToDelete={resolvedAnnotationIdToDelete}
              />
            </Box>
          )}

          {!isReadOnly && isInFinalizedCreateMode && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
                border: `1px solid ${theme.palette.divider}`,
                p: theme.spacing(4),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                <Typography variant="body1" sx={{ fontWeight: '500' }}>
                  Supervisor
                </Typography>
                <Chip
                  variant="filled"
                  sx={{ backgroundColor: theme.palette.grey[200], fontSize: '13px' }}
                  label={formatMessage({
                    id: 'workspaceCommon.annotationConfig.conceptCard.inProgress',
                  })}
                />
              </Box>
              <InProgressAnnotationCard
                annotation={null}
                conceptId={resolvedInConcept.conceptId}
                isFinalizedCreate
              />
            </Box>
          )}

          {!isReadOnly && shouldShowCreateButton && !isInResolvedReplaceMode && (
            <Button
              id="add-finalized-annotation-button"
              startIcon={<AddIcon />}
              displayLabel={formatMessage({
                id: 'annotationContent.annotationConfig.conceptCard.createAnnotationButton',
              })}
              variant="outlined"
              sx={{
                textTransform: 'none',
                width: '100%',
                fontSize: '14px',
                color: theme.palette.primary.main,
              }}
              onClickHandler={handleStartFinalizedCreate}
            />
          )}
        </Box>
      )}
      <ConfirmDeleteModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        itemLabel="annotation"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleteResolvedAnnotationLoading}
      />
    </Paper>
  );
};
